import {
    Field,
    Input,
    Radio,
    RadioGroup,
    RadioGroupOnChangeData
} from '@fluentui/react-components';
import { usePhoneNumberInputStyles } from './PhoneNumberInputValidate.styles';
import StatusInfoBox from 'components/core/StatusInfoBox/StatusInfoBox';
import { useMemo, useState } from 'react';
import { IByodPhoneNumberGrouping } from './PhoneNumberInputValidate.api';
import { useCart } from '../../../../contexts/CartContext/CartContext.hooks';
import { ILineItem } from '../../../../contexts/CartContext/CartContext.types';
import KeepNumberAgreementInput from 'components/forms/inputs/KeepNumberAgreement';

export interface IPhoneNumberInputValidateProps {
    lineItem: ILineItem;
    validPhoneNumberGroups: IByodPhoneNumberGrouping[];
}

export interface PhoneNumberValidation {
    isValid: boolean;
    message: string;
}

const PhoneNumberInputValidate = (props: IPhoneNumberInputValidateProps) => {
    const { cart, updateLineItems } = useCart();
    const { lineItem, validPhoneNumberGroups } = props;
    const styles = usePhoneNumberInputStyles();

    const phoneNumberPattern = /^[+]?[(]?\d{3}[)]?[-\s.]?\d{3}[-\s.]?\d{4,6}$/;

    const [phoneNumber, setPhoneNumber] = useState(lineItem.phoneNumberInformation.oldNumber ?? '');
    const [showPhoneNumberValidation, setShowPhoneNumberValidation] = useState(
        !lineItem.phoneNumberInformation.needsNewNumber
    );
    const [isValidPhoneNumber, setIsValidPhoneNumber] = useState<PhoneNumberValidation>();

    const updateNeedsNewNumber = (needsNewNumber: boolean) => {
        const lineCart = cart.lineItems.map((x) => {
            if (JSON.stringify(x) === JSON.stringify(lineItem)) {
                x.phoneNumberInformation.needsNewNumber = needsNewNumber;
            }
            return x;
        });
        updateLineItems(lineCart);
    };

    const updateOldNumber = (oldNumber?: string) => {
        const lineCart = cart.lineItems.map((x) => {
            if (JSON.stringify(x) === JSON.stringify(lineItem)) {
                x.phoneNumberInformation.oldNumber = oldNumber;
            }
            return x;
        });
        updateLineItems(lineCart);
    };

    const updateAgreement = (phoneNumberAgreement?: boolean) => {
        const lineCart = cart.lineItems.map((x) => {
            if (JSON.stringify(x) === JSON.stringify(lineItem)) {
                x.phoneNumberInformation.acceptedPhoneNumberAgreement = phoneNumberAgreement;
            }
            return x;
        });
        updateLineItems(lineCart);
    };

    const validateInput = (input: string) => {
        if (phoneNumberPattern.test(input)) {
            const validated = validPhoneNumberGroups.filter((number) => {
                const strippedPhoneNumber = input.replace(/\D/g, '');

                if (
                    number.prefix &&
                    !strippedPhoneNumber.substring(3, 8).includes(number.prefix.toString())
                )
                    return false;
                return strippedPhoneNumber.substring(0, 5).includes(number.areaCode.toString());
            });

            if (validated.length == 0) {
                updateOldNumber();
                setIsValidPhoneNumber({
                    isValid: false,
                    message: 'Your number cannot be transferred to Viaero.'
                });
                return;
            }
        } else {
            updateOldNumber();
            setIsValidPhoneNumber({
                isValid: false,
                message: 'The phone number you have entered is invalid.'
            });
            return;
        }

        updateOldNumber(input);
        setIsValidPhoneNumber({
            isValid: true,
            message: 'Your number can be transferred to Viaero.'
        });
    };

    const handleRadioSelection = (data: RadioGroupOnChangeData) => {
        if (data.value === 'Yes') {
            setShowPhoneNumberValidation(true);

            updateNeedsNewNumber(false);
        } else {
            setPhoneNumber('');
            setShowPhoneNumberValidation(false);

            updateNeedsNewNumber(true);
            updateOldNumber();
            updateAgreement();
            updateAgreement();
        }
    };

    const phoneNumberValidation = useMemo(() => {
        return showPhoneNumberValidation ? (
            <div>
                <Input
                    placeholder='Phone Number'
                    onChange={(_, data) => {
                        setPhoneNumber(data.value);
                        validateInput(data.value);
                    }}
                    data-testid='input-phone-number-test'
                    value={phoneNumber}
                    className={styles.phoneNumberInput}
                />
                {phoneNumber.replace(/\D/g, '').length >= 10 && isValidPhoneNumber && (
                    <StatusInfoBox
                        isSuccessful={isValidPhoneNumber.isValid}
                        text={isValidPhoneNumber.message}
                    />
                )}
                <KeepNumberAgreementInput
                    onChange={(value) => {
                        updateAgreement(value);
                    }}
                    defaultChecked={lineItem.phoneNumberInformation.acceptedPhoneNumberAgreement}
                />
            </div>
        ) : (
            <></>
        );
    }, [showPhoneNumberValidation, phoneNumber, isValidPhoneNumber, lineItem]);

    return (
        <div>
            <Field label='Want to keep your number?'>
                <RadioGroup
                    onChange={(_, data) => {
                        handleRadioSelection(data);
                    }}
                    defaultValue={lineItem.phoneNumberInformation.needsNewNumber ? 'No' : 'Yes'}
                    layout='horizontal'
                >
                    <Radio value='Yes' label='Yes' />
                    <Radio value='No' label='No' />
                </RadioGroup>
            </Field>
            {phoneNumberValidation}
        </div>
    );
};

export default PhoneNumberInputValidate;
